import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import {
  CARRIER_HAZMAT,
  DHL_ECOMMERCE_HAZMAT_MAP,
  Hazmat,
  HAZMAT_DISPLAY_LABELS,
  isSupportedHazmat,
} from "@redotech/redo-fulfillment-carrier-common/hazmat";
import { RedoShopifyDeliveryMethodType } from "@redotech/redo-fulfillment-group-common/fulfillment-delivery-method-type";
import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { isOpenIsh } from "@redotech/redo-fulfillment-group-common/fulfillment-group-status";
import { WeightFormat } from "@redotech/redo-fulfillment-outbound-labels-common/outbound-label-enums";
import { WeightUnit } from "@redotech/redo-fulfillment-outbound-labels-common/util";
import { OutboundParcel } from "@redotech/redo-fulfillment-parcel-common/parcel";
import {
  CANADA_POST_OPTION_LABELS,
  CanadaPostOption,
  includesDhlBpm,
  includesLibraryMail,
  includesMediaMail,
  PaymentResponsibility,
  RateProvider,
  ShipmentPaymentOption,
  ShipmentPaymentOptionType,
  SpecialRateEligibility,
} from "@redotech/redo-fulfillment-rate-common/outbound-labels";
import {
  DhlEcommerceServiceEndorsement,
  type DhlEcommerceLabelOptions,
} from "@redotech/redo-fulfillment-rate-common/outbound-shipment";
import { RedoShopifyCountryCode } from "@redotech/redo-inventory-common/fulfillment-country-code";
import { TotalWeightWidget } from "@redotech/redo-merchant-app-common/components/weight-widget/total-weight-widget";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { FulfillmentGroupUpdate } from "@redotech/redo-merchant-fulfillment-rpc/schema/update-fulfillment-groups";
import { toast } from "@redotech/redo-web/alert";
import { RedoCheckbox } from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox";
import { DeprecatedDatePicker } from "@redotech/redo-web/arbiter-components/date-picker/redo-date-picker";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoExternalLink } from "@redotech/redo-web/arbiter-components/links/redo-external-link";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { Flex } from "@redotech/redo-web/flex";
import { InfoTooltip } from "@redotech/redo-web/info-tooltip";
import { Text } from "@redotech/redo-web/text";
import { DisabledTooltip } from "@redotech/redo-web/tooltip/disabled-tooltip";
import { memo, useCallback, useMemo, useState } from "react";
import { AddThirdPartyBillingModal } from "../setting/carrier-accounts/add-third-party-billing-modal";
import { AddThirdPartyBillingAccountData } from "../setting/carrier-accounts/third-party-billing";
import * as additionalFulfillmentCss from "./additional-fulfillment-settings.module.css";
import { ParcelEditSelect } from "./parcels/parcel-edit-select";

const TOMORROW = new Date();
TOMORROW.setDate(TOMORROW.getDate() + 1);

function getFulfillmentDateAtReleaseHour(
  date: Date,
  configuredReleaseHour: number | null | undefined,
): Date {
  // Use the team's notification release hour when available; otherwise default to 9 AM merchant time.
  const releaseHour =
    typeof configuredReleaseHour === "number" &&
    configuredReleaseHour >= 0 &&
    configuredReleaseHour <= 23
      ? configuredReleaseHour
      : 9;
  const fulfillmentDate = new Date(date);
  fulfillmentDate.setHours(releaseHour, 0, 0, 0);
  return fulfillmentDate;
}

function skipsMarketplaceNotification(group: FulfillmentGroupData): boolean {
  return isOpenIsh(group.status)
    ? group.notifyMarketplace === false
    : group.marketplaceNotification?.status === "skipped";
}

const dhlEcommerceHazmatValues: readonly Hazmat[] =
  CARRIER_HAZMAT.DHL_ECOMMERCE;

const dhlLimitedQuantityCanadaMexicoHazmat = Hazmat.LIMITED_QUANTITY;

const dhlLimitedQuantityCountries = new Set<string>([
  RedoShopifyCountryCode.Ca,
  RedoShopifyCountryCode.Mx,
]);

function getHazmatDropdownLabel(hazmat: Hazmat) {
  if (dhlEcommerceHazmatValues.includes(hazmat)) {
    const dhlHazmat = hazmat as (typeof CARRIER_HAZMAT.DHL_ECOMMERCE)[number];
    return [
      `DG ${DHL_ECOMMERCE_HAZMAT_MAP[dhlHazmat]}`,
      HAZMAT_DISPLAY_LABELS[dhlHazmat],
    ].join(" - ");
  }

  return HAZMAT_DISPLAY_LABELS[hazmat as keyof typeof HAZMAT_DISPLAY_LABELS];
}

function getDestinationCountryCode(group: FulfillmentGroupData) {
  return group.verifiedDestinationAddress?.verified
    ? group.verifiedDestinationAddress.countryCode
    : group.destinationAddress?.countryCode;
}

function getOriginCountryCode(group: FulfillmentGroupData) {
  return group.verifiedOriginAddress?.verified
    ? group.verifiedOriginAddress.countryCode
    : group.originAddress?.countryCode;
}

function isInternationalGroup(group: FulfillmentGroupData) {
  const origin = getOriginCountryCode(group);
  const destination = getDestinationCountryCode(group);
  return !!origin && !!destination && origin !== destination;
}

const canadaPostOptions: RedoListItem<CanadaPostOption | null>[] = [
  { id: "none", type: "text", text: "None", value: null },
  ...Object.values(CanadaPostOption).map(
    (option): RedoListItem<CanadaPostOption | null> => ({
      id: option,
      type: "text",
      text: CANADA_POST_OPTION_LABELS[option],
      value: option,
    }),
  ),
];

const defaultThirdPartyBillingAccount: RedoListItem<string | null> = {
  id: "no-third-party-billing",
  type: "text",
  text: "No Third Party Billing",
  value: null,
};

const dhlServiceEndorsementLabels: Record<
  DhlEcommerceServiceEndorsement,
  string
> = {
  [DhlEcommerceServiceEndorsement.AddressForwardingRequested]:
    "1 - Address forwarding requested",
  [DhlEcommerceServiceEndorsement.ForwardingServiceRequested]:
    "2 - Forwarding service requested",
  [DhlEcommerceServiceEndorsement.ChangeServiceRequested]:
    "3 - Change service requested",
  [DhlEcommerceServiceEndorsement.ReturnServiceRequested]:
    "4 - Return service requested",
};

const dhlServiceEndorsementOptions: RedoListItem<DhlEcommerceServiceEndorsement | null>[] =
  [
    { id: "none", type: "text", text: "None", value: null },
    ...Object.values(DhlEcommerceServiceEndorsement).map(
      (endorsement): RedoListItem<DhlEcommerceServiceEndorsement | null> => ({
        id: endorsement,
        type: "text",
        text: dhlServiceEndorsementLabels[endorsement],
        value: endorsement,
      }),
    ),
  ];

function cleanDhlCustomLabelText(value: string | null | undefined) {
  return value?.trim() || null;
}

function cleanDhlEcommerceLabelOptions(
  options: DhlEcommerceLabelOptions,
): DhlEcommerceLabelOptions | null {
  const cleanedOptions: DhlEcommerceLabelOptions = {
    customLabelText1: cleanDhlCustomLabelText(options.customLabelText1),
    customLabelText2: cleanDhlCustomLabelText(options.customLabelText2),
    serviceEndorsement: options.serviceEndorsement ?? null,
  };

  return cleanedOptions.customLabelText1 ||
    cleanedOptions.customLabelText2 ||
    cleanedOptions.serviceEndorsement
    ? cleanedOptions
    : null;
}

export const AdditionalFulfillmentSettings = memo(
  function AdditionalFulfillmentSettings({
    updateRows,
    fulfillmentGroups,
    fulfillmentDate,
    everythingDisabled,
    isEditingLockedStatus,
    parcelBeingEditedPerGroup,
    updateParcel,
    disableBulkSettingsEditing,
  }: {
    updateRows: (updates: FulfillmentGroupUpdate[]) => void;
    fulfillmentGroups: readonly FulfillmentGroupData[];
    fulfillmentDate: Date | null;
    everythingDisabled: boolean;
    isEditingLockedStatus: boolean;
    parcelBeingEditedPerGroup: OutboundParcel[];
    updateParcel: (parcel: Partial<OutboundParcel>) => void;
    disableBulkSettingsEditing?: boolean;
  }) {
    const merchantRpcClient = useRequiredContext(RedoMerchantRpcClientContext);

    // Third party billing state
    const [billingAccountsReloadKey, setBillingAccountsReloadKey] =
      useState<symbol>(Symbol());
    const [addBillingModalOpen, setAddBillingModalOpen] = useState(false);

    const thirdPartyBillingAccounts = useLoad(async () => {
      const result = await merchantRpcClient.getThirdPartyBillingAccounts(null);
      return result.accounts;
    }, [merchantRpcClient, billingAccountsReloadKey]);

    const accounts = useMemo(
      () => thirdPartyBillingAccounts.value ?? [],
      [thirdPartyBillingAccounts.value],
    );

    const carrierAccounts = useLoad(
      async () => await merchantRpcClient.getCarrierAccounts(null),
      [merchantRpcClient],
    );

    const hasDhlByoaAccount = useMemo(
      () =>
        carrierAccounts.value?.merchantCarrierAccounts.some(
          (account) => account.provider === RateProvider.DHL_ECOMMERCE,
        ) ?? false,
      [carrierAccounts.value],
    );

    const { selectedThirdPartyBillingAccount, mixedThirdPartyBillingAccounts } =
      useMemo(() => {
        const ids = fulfillmentGroups.map((group) => {
          const opt = group.shipmentPaymentOption;
          if (opt?.type === ShipmentPaymentOptionType.THIRD_PARTY) {
            // We should find the exact account by id. We used to just use the account number, country, and postal code.
            // We can't really be doing that because accounts can have the same account number, country, and postal code.
            // This is more relevent when using a temporary (hidden) account - that might end up getting used again
            // Only really matters with UI display
            if (opt.thirdPartyBillingAccountId) {
              return opt.thirdPartyBillingAccountId;
            }
            // Kept for backwards compatibility
            return (
              accounts.find(
                (a) =>
                  a.accountNumber === opt.account &&
                  a.country === opt.country &&
                  a.postalCode === opt.postal_code,
              )?._id ?? null
            );
          }
          return null;
        });
        const isMixed = new Set(ids).size > 1;
        const accountId = isMixed ? null : (ids[0] ?? null);
        return {
          selectedThirdPartyBillingAccount: accountId
            ? (accounts.find((a) => a._id === accountId) ?? null)
            : null,
          mixedThirdPartyBillingAccounts: isMixed,
        };
      }, [fulfillmentGroups, accounts]);

    const splitDutyAndVatCheckboxValue = useMemo(() => {
      if (mixedThirdPartyBillingAccounts) {
        return "indeterminate" as const;
      }
      if (!selectedThirdPartyBillingAccount) {
        return false;
      }
      return fulfillmentGroups.every((group) =>
        group.shipmentPaymentOption?.explicit_payment_responsibilities?.includes(
          PaymentResponsibility.VAT,
        ),
      );
    }, [
      fulfillmentGroups,
      selectedThirdPartyBillingAccount,
      mixedThirdPartyBillingAccounts,
    ]);

    const onShipmentPaymentOptionChange = useCallback(
      (
        accountId: string | null,
        paymentResponsibilities: PaymentResponsibility[],
      ) => {
        const account = accounts.find((a) => a._id === accountId);
        const shipmentPaymentOption: ShipmentPaymentOption | null =
          account && accountId
            ? {
                type: ShipmentPaymentOptionType.THIRD_PARTY,
                carrier: account.carrier,
                account: account.accountNumber,
                country: account.country,
                postal_code: account.postalCode,
                thirdPartyBillingAccountId: account._id,
                explicit_payment_responsibilities: paymentResponsibilities,
              }
            : null;
        updateRows(
          fulfillmentGroups.map((group) => ({
            fulfillmentGroupId: group._id,
            shipmentPaymentOption,
          })),
        );
      },
      [accounts, fulfillmentGroups, updateRows],
    );

    const handleAddAccount = useCallback(
      async (
        accountData: AddThirdPartyBillingAccountData,
        options?: { hidden?: boolean },
      ) => {
        const hidden = options?.hidden ?? false;
        try {
          const result = await merchantRpcClient.createThirdPartyBillingAccount(
            { ...accountData, hidden },
          );
          const account = result.account;
          setBillingAccountsReloadKey(Symbol());
          const shipmentPaymentOption: ShipmentPaymentOption = {
            type: ShipmentPaymentOptionType.THIRD_PARTY,
            carrier: account.carrier,
            account: account.accountNumber,
            country: account.country,
            postal_code: account.postalCode,
            thirdPartyBillingAccountId: account._id,
            explicit_payment_responsibilities: [],
          };
          updateRows(
            fulfillmentGroups.map((group) => ({
              fulfillmentGroupId: group._id,
              shipmentPaymentOption,
            })),
          );
          return { success: true };
        } catch {
          toast("Failed to create third party billing account", {
            variant: "error",
          });
          return { success: false };
        }
      },
      [merchantRpcClient, fulfillmentGroups, updateRows],
    );

    const isShipping = useMemo(() => {
      return fulfillmentGroups.every(
        (group) =>
          group.deliveryMethod === RedoShopifyDeliveryMethodType.Shipping,
      );
    }, [fulfillmentGroups]);

    const isMultiParcel = useMemo(() => {
      return fulfillmentGroups.some(
        (group) =>
          group.selectedParcels?.length && group.selectedParcels.length > 1,
      );
    }, [fulfillmentGroups]);

    const team = useRequiredContext(TeamContext);
    const updateMarketplaceFulfillmentSettings = useHandler(
      (settings: {
        notifyMarketplace?: boolean;
        fulfillmentDate?: Date | null;
      }) => {
        updateRows(
          fulfillmentGroups.map(({ _id }) => ({
            fulfillmentGroupId: _id,
            ...settings,
          })),
        );
      },
    );
    const updateFulfillmentDate = useHandler((date: Date | null) => {
      const fulfillmentDateAtReleaseHour = date
        ? getFulfillmentDateAtReleaseHour(
            date,
            team.settings.outboundLabels?.fulfillmentNotificationReleaseHour,
          )
        : null;
      updateMarketplaceFulfillmentSettings({
        fulfillmentDate: fulfillmentDateAtReleaseHour,
      });
    });

    const { signatureRequired, mixedSignatureRequired } =
      fulfillmentGroups.every(
        (group) =>
          !!group.signatureRequired ===
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          !!fulfillmentGroups[0].signatureRequired,
      )
        ? {
            // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
            signatureRequired: fulfillmentGroups[0].signatureRequired,
            mixedSignatureRequired: false,
          }
        : { signatureRequired: null, mixedSignatureRequired: true };

    const { adultSignatureRequired, mixedAdultSignatureRequired } =
      fulfillmentGroups.every(
        (group) =>
          !!group.adultSignatureRequired ===
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          !!fulfillmentGroups[0].adultSignatureRequired,
      )
        ? {
            // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
            adultSignatureRequired: fulfillmentGroups[0].adultSignatureRequired,
            mixedAdultSignatureRequired: false,
          }
        : { adultSignatureRequired: null, mixedAdultSignatureRequired: true };

    const { ddp, mixedDdp } = fulfillmentGroups
      .map((group) => group.ddp)
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      .every((ddp) => !!ddp === !!fulfillmentGroups[0].ddp)
      ? // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        { ddp: fulfillmentGroups[0].ddp, mixedDdp: false }
      : { ddp: null, mixedDdp: true };

    const canUseDhlLimitedQuantityCanadaMexico = fulfillmentGroups.every(
      (group) => {
        const countryCode = getDestinationCountryCode(group);
        return (
          countryCode != null && dhlLimitedQuantityCountries.has(countryCode)
        );
      },
    );

    const dhlEcommerceEnabled =
      (team.settings.outboundLabels?.enabledCarrierAccounts
        ?.directToCarrierIntegrations?.dhl ||
        team.settings.outboundLabels?.enabledCarrierAccounts
          ?.directToCarrierIntegrations?.dhlInternational) ??
      false;

    const dhlEcommerceHazmatOptions = CARRIER_HAZMAT.DHL_ECOMMERCE.filter(
      (hazmat) =>
        dhlEcommerceEnabled &&
        (hazmat !== dhlLimitedQuantityCanadaMexicoHazmat ||
          canUseDhlLimitedQuantityCanadaMexico),
    );

    const mediaMailCheckboxValue = fulfillmentGroups.every((group) =>
      includesMediaMail(group.specialRateEligibility),
    )
      ? true
      : fulfillmentGroups.some((group) =>
            includesMediaMail(group.specialRateEligibility),
          )
        ? "indeterminate"
        : false;

    const dhlBpmCheckboxValue = fulfillmentGroups.every((group) =>
      includesDhlBpm(group.specialRateEligibility),
    )
      ? true
      : fulfillmentGroups.some((group) =>
            includesDhlBpm(group.specialRateEligibility),
          )
        ? "indeterminate"
        : false;

    /*
    const libraryMailCheckboxValue = useMemo(() => {
      const someHaveLibraryMail = fulfillmentGroups.some((group) =>
        includesLibraryMail(group.specialRateEligibility),
      );
      if (!someHaveLibraryMail) return false;

      const allHaveLibraryMail = fulfillmentGroups.every((group) =>
        includesLibraryMail(group.specialRateEligibility),
      );

      return allHaveLibraryMail ? true : "indeterminate";
    }, [fulfillmentGroups]);
    */

    // Check if all groups have the same hazmat value for the selected parcel
    const hazmatValue = parcelBeingEditedPerGroup.every(
      (parcel) => parcel.hazmat,
    )
      ? // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        parcelBeingEditedPerGroup[0].hazmat
      : null;

    // Null if mixed weights.
    const dryIceWeights = parcelBeingEditedPerGroup.map(
      (parcel) => parcel.dryIceWeight ?? { unit: WeightUnit.OUNCE, value: 6 },
    );

    // TODO: consider "equivalent" weights of different units equal.
    // "Equivalent" meaning we don't care to split ounces.
    const dryIceWeight = dryIceWeights.every(
      (parcel) =>
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        parcel.value === dryIceWeights[0].value &&
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        parcel.unit === dryIceWeights[0].unit,
    )
      ? dryIceWeights[0]
      : null;

    const saturdayDeliveryCheckboxValue = fulfillmentGroups.every(
      (group) => group?.saturdayDelivery === true,
    )
      ? true
      : fulfillmentGroups.some((group) => group?.saturdayDelivery === true)
        ? "indeterminate"
        : false;

    const fulfillmentDateCheckboxValue = fulfillmentGroups.every(
      (group) => group.fulfillmentDate != null,
    )
      ? true
      : fulfillmentGroups.some((group) => group.fulfillmentDate != null)
        ? "indeterminate"
        : false;

    const notifyMarketplaceCheckboxValue = fulfillmentGroups.every(
      skipsMarketplaceNotification,
    )
      ? true
      : fulfillmentGroups.some(skipsMarketplaceNotification)
        ? "indeterminate"
        : false;

    const buyReturnLabelCheckboxValue = fulfillmentGroups.every(
      (group) => group?.buyReturnLabel === true,
    )
      ? true
      : fulfillmentGroups.some((group) => group?.buyReturnLabel === true)
        ? "indeterminate"
        : false;

    const sendLabelPurchasedNotificationCheckboxValue = fulfillmentGroups.every(
      (group) => group?.sendLabelPurchasedNotification === true,
    )
      ? true
      : fulfillmentGroups.some(
            (group) => group?.sendLabelPurchasedNotification === true,
          )
        ? "indeterminate"
        : false;

    const carbonNeutralCheckboxValue = fulfillmentGroups.every(
      (group) => group?.carbonNeutral === true,
    )
      ? true
      : fulfillmentGroups.some((group) => group?.carbonNeutral === true)
        ? "indeterminate"
        : false;

    const canadaPostOptionValues = fulfillmentGroups.map(
      (group) => group?.canadaPostOption ?? null,
    );
    const canadaPostOptionValue = canadaPostOptionValues.every(
      (v) => v === canadaPostOptionValues[0],
    )
      ? (canadaPostOptionValues[0] ?? null)
      : null;

    const singleFulfillmentGroup =
      fulfillmentGroups.length === 1 ? fulfillmentGroups[0] : null;
    const dhlEcommerceLabelOptions =
      singleFulfillmentGroup?.dhlEcommerceLabelOptions;

    const updateDhlEcommerceLabelOptions = useCallback(
      (options: Partial<DhlEcommerceLabelOptions>) => {
        if (!singleFulfillmentGroup) {
          return;
        }
        updateRows([
          {
            fulfillmentGroupId: singleFulfillmentGroup._id,
            dhlEcommerceLabelOptions: cleanDhlEcommerceLabelOptions({
              ...singleFulfillmentGroup.dhlEcommerceLabelOptions,
              ...options,
            }),
          },
        ]);
      },
      [singleFulfillmentGroup, updateRows],
    );

    const directDeliveryOnlyCheckboxValue = fulfillmentGroups.every(
      (group) => group?.directDeliveryOnly === true,
    )
      ? true
      : fulfillmentGroups.some((group) => group?.directDeliveryOnly === true)
        ? "indeterminate"
        : false;

    const overrideResidentialAddressCheckboxValue = fulfillmentGroups.every(
      (group) => group?.overrideResidentialAddress === true,
    )
      ? true
      : fulfillmentGroups.some(
            (group) => group?.overrideResidentialAddress === true,
          )
        ? "indeterminate"
        : false;

    const showFulfillmentDateInfo = useMemo(() => {
      const sevenDaysFromNow = new Date();
      sevenDaysFromNow.setDate(sevenDaysFromNow.getDate() + 7);
      return fulfillmentGroups.some(
        (group) =>
          group.fulfillmentDate != null &&
          group.fulfillmentDate > sevenDaysFromNow,
      );
    }, [fulfillmentGroups]);

    const canadaPostEnabled =
      team.settings.outboundLabels?.enabledCarrierAccounts
        ?.directToCarrierIntegrations?.canadaPost ?? false;

    const someGroupHasLandedCost = fulfillmentGroups.some(
      (group) => group.hasLandedCost,
    );
    const allGroupsHaveLandedCost = fulfillmentGroups.every(
      (group) => group.hasLandedCost,
    );

    // "Always ship with LCG" forces DDP, but only on international shipments.
    const forceLcgDdp =
      !!team.settings.landedCost?.fulfillUsingLcgAlways &&
      fulfillmentGroups.every(isInternationalGroup);

    const ddpDisabledTooltip = forceLcgDdp
      ? "DDP is required because this store always ships international orders with landed cost guarantee"
      : allGroupsHaveLandedCost
        ? "All selections have DDP due to landed cost"
        : someGroupHasLandedCost
          ? "Some selections have DDP due to landed cost"
          : null;

    return (
      <Flex dir="column" gap="2xl">
        <ParcelEditSelect />

        {disableBulkSettingsEditing ? (
          <Flex
            bgColor="secondary"
            borderColor="tertiary"
            borderStyle="solid"
            borderWidth="1px"
            p="xl"
            radius="md"
          >
            <Text color="tertiary" fontSize="sm" textAlign="center">
              Bulk settings editing is not available for orders with multiple
              packages. Select a single order to edit settings.
            </Text>
          </Flex>
        ) : (
          <Flex dir="column" gap="md" justify="flex-start">
            <RedoToggle
              disabled={everythingDisabled || isEditingLockedStatus}
              label="This package contains hazardous materials"
              setValue={(value) => {
                if (!value) {
                  updateParcel({ hazmat: null, dryIceWeight: null });
                } else if (hazmatValue == null) {
                  updateParcel({ hazmat: Hazmat.CLASS_9_UNMARKED_LITHIUM });
                }
              }}
              size="xs"
              value={hazmatValue != null}
            />

            {hazmatValue != null && (
              <Flex dir="column" gap="md">
                <RedoSingleSelectDropdownInput<Hazmat>
                  disabled={everythingDisabled || isEditingLockedStatus}
                  label="Hazmat type"
                  options={[
                    {
                      id: "fedex-dhl-section",
                      type: "section" as const,
                      text: "FedEx / DHL",
                      value: Hazmat.CLASS_9_UNMARKED_LITHIUM, // Unused as this is a section
                    },
                    ...CARRIER_HAZMAT.FEDEX_DHL.map((hazmat) => ({
                      id: hazmat,
                      type: "text" as const,
                      text: getHazmatDropdownLabel(hazmat),
                      value: hazmat,
                    })),
                    ...(dhlEcommerceHazmatOptions.length
                      ? [
                          {
                            id: "dhl-ecommerce-section",
                            type: "section" as const,
                            text: "DHL eCommerce",
                            value: Hazmat.ORMD, // Unused as this is a section
                          },
                        ]
                      : []),
                    ...dhlEcommerceHazmatOptions.map((hazmat) => ({
                      id: hazmat,
                      type: "text" as const,
                      text: getHazmatDropdownLabel(hazmat),
                      value: hazmat,
                    })),
                    {
                      id: "usps-section",
                      type: "section" as const,
                      text: "USPS",
                      value: Hazmat.CLASS_9_UNMARKED_LITHIUM, // Unused as this is a section
                    },
                    ...CARRIER_HAZMAT.USPS.map((hazmat) => ({
                      id: hazmat,
                      type: "text" as const,
                      text: getHazmatDropdownLabel(hazmat),
                      value: hazmat,
                    })),
                    {
                      id: "all-carriers-section",
                      type: "section" as const,
                      text: "All Carriers",
                      value: Hazmat.CLASS_9_DRY_ICE, // Unused as this is a section
                    },
                    ...CARRIER_HAZMAT.ALL_CARRIERS.map((hazmat) => ({
                      id: hazmat,
                      type: "text" as const,
                      text: getHazmatDropdownLabel(hazmat),
                      value: hazmat,
                    })),
                  ]}
                  optionSelected={({ item }) => {
                    if (item?.value && item.type !== "section") {
                      updateParcel({
                        hazmat: item.value,
                        dryIceWeight:
                          item.value === Hazmat.CLASS_9_DRY_ICE
                            ? { unit: WeightUnit.OUNCE, value: 6 }
                            : null,
                      });
                    }
                  }}
                  selectedItem={
                    hazmatValue &&
                    isSupportedHazmat(hazmatValue) &&
                    (!dhlEcommerceHazmatValues.includes(hazmatValue) ||
                      dhlEcommerceEnabled) &&
                    (hazmatValue !== dhlLimitedQuantityCanadaMexicoHazmat ||
                      canUseDhlLimitedQuantityCanadaMexico)
                      ? {
                          id: hazmatValue,
                          type: "text" as const,
                          text: getHazmatDropdownLabel(hazmatValue),
                          value: hazmatValue,
                        }
                      : undefined
                  }
                  size="xs"
                />

                {hazmatValue === Hazmat.CLASS_9_DRY_ICE && (
                  <Flex w="full">
                    <TotalWeightWidget
                      disabled={everythingDisabled || isEditingLockedStatus}
                      hideScale
                      onWeightChange={(weight) => {
                        updateParcel({ dryIceWeight: weight });
                      }}
                      title=""
                      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
                      weight={dryIceWeight}
                      weightFormat={
                        team?.settings?.outboundLabels?.weightFormat ??
                        WeightFormat.IMPERIAL
                      }
                    />
                  </Flex>
                )}
              </Flex>
            )}
          </Flex>
        )}

        {/* Divider between per-parcel and per-shipment settings */}
        <Flex
          borderBottomWidth="1px"
          borderColor="primary"
          borderStyle="solid"
          w="full"
        />
        <Flex>
          <RedoCheckbox
            description={
              <Flex onClick={(e) => e.preventDefault()} pt="md">
                <DeprecatedDatePicker
                  buttonSize="sm"
                  date={fulfillmentDate ?? undefined}
                  disabled={everythingDisabled || isEditingLockedStatus}
                  minDate={TOMORROW}
                  setDate={updateFulfillmentDate}
                />
              </Flex>
            }
            disabled={everythingDisabled || isEditingLockedStatus}
            label="Delay fulfillment until..."
            setValue={(value) => {
              if (value === true) {
                updateMarketplaceFulfillmentSettings({
                  notifyMarketplace: true,
                  fulfillmentDate: getFulfillmentDateAtReleaseHour(
                    TOMORROW,
                    team.settings.outboundLabels
                      ?.fulfillmentNotificationReleaseHour,
                  ),
                });
              } else {
                updateFulfillmentDate(null);
              }
            }}
            size="xs"
            value={fulfillmentDateCheckboxValue}
          />
          {showFulfillmentDateInfo && (
            <InfoTooltip
              boxSize={16}
              tooltip={{
                title:
                  "Not all carriers accept a label date more than 7 days in the future.",
              }}
            />
          )}
        </Flex>
        <Flex>
          <RedoCheckbox
            description="Order updates made in Redo will not be reflected in the marketplace the order originated in."
            disabled={everythingDisabled || isEditingLockedStatus}
            label="Do not notify marketplace"
            setValue={(value) => {
              const doNotNotifyMarketplace =
                value === "indeterminate" ? false : value;
              updateMarketplaceFulfillmentSettings({
                notifyMarketplace: !doNotNotifyMarketplace,
              });
            }}
            size="xs"
            value={notifyMarketplaceCheckboxValue}
          />
        </Flex>
        <Flex>
          <RedoCheckbox
            description="Send the customer the 'label purchased' notification flow when the shipping label is purchased during fulfillment."
            disabled={everythingDisabled || isEditingLockedStatus}
            label="Send label purchased notification"
            setValue={(value) => {
              updateRows(
                fulfillmentGroups.map(({ _id }) => ({
                  fulfillmentGroupId: _id,
                  sendLabelPurchasedNotification:
                    value === "indeterminate" ? false : value,
                })),
              );
            }}
            size="xs"
            value={sendLabelPurchasedNotificationCheckboxValue}
          />
        </Flex>

        {isShipping && (
          <>
            <Flex>
              <RedoCheckbox
                disabled={everythingDisabled || isEditingLockedStatus}
                label="Require a signature on delivery"
                setValue={(value) => {
                  updateRows(
                    fulfillmentGroups.map(({ _id }) => ({
                      fulfillmentGroupId: _id,
                      signatureRequired:
                        value === "indeterminate" ? false : value,
                      adultSignatureRequired: false,
                    })),
                  );
                }}
                size="xs"
                value={
                  mixedSignatureRequired
                    ? "indeterminate"
                    : (signatureRequired ?? false)
                }
              />
            </Flex>

            <Flex>
              <RedoCheckbox
                disabled={everythingDisabled || isEditingLockedStatus}
                label="Require an adult signature on delivery"
                setValue={(value) => {
                  updateRows(
                    fulfillmentGroups.map(({ _id }) => ({
                      fulfillmentGroupId: _id,
                      signatureRequired: false,
                      adultSignatureRequired:
                        value === "indeterminate" ? false : value,
                    })),
                  );
                }}
                size="xs"
                value={
                  mixedAdultSignatureRequired
                    ? "indeterminate"
                    : (adultSignatureRequired ?? false)
                }
              />
            </Flex>

            <Flex>
              <DisabledTooltip
                disabledMessage={ddpDisabledTooltip ?? undefined}
              >
                <RedoCheckbox
                  disabled={
                    everythingDisabled ||
                    isEditingLockedStatus ||
                    someGroupHasLandedCost ||
                    forceLcgDdp
                  }
                  label="Bill duties and taxes to payer of shipping charges (DDP)"
                  setValue={(value) => {
                    updateRows(
                      fulfillmentGroups.map(({ _id }) => ({
                        fulfillmentGroupId: _id,
                        ddp: value === "indeterminate" ? false : value,
                      })),
                    );
                  }}
                  size="xs"
                  value={
                    forceLcgDdp
                      ? true
                      : mixedDdp
                        ? "indeterminate"
                        : (ddp ?? false)
                  }
                />
              </DisabledTooltip>
            </Flex>

            <Flex dir="column" gap="sm" justify="flex-start">
              <RedoCheckbox
                disabled={everythingDisabled || isEditingLockedStatus}
                label="Saturday delivery"
                setValue={(value) => {
                  updateRows(
                    fulfillmentGroups.map(({ _id }) => ({
                      fulfillmentGroupId: _id,
                      saturdayDelivery: !!value,
                    })),
                  );
                }}
                size="xs"
                value={saturdayDeliveryCheckboxValue}
              />
            </Flex>

            <Flex dir="column" gap="sm" justify="flex-start">
              <RedoCheckbox
                description="By default the label will use the same package settings as the original fulfillment"
                disabled={
                  everythingDisabled || isMultiParcel || isEditingLockedStatus
                }
                label="Create prepaid return label"
                setValue={(value) => {
                  updateRows(
                    fulfillmentGroups.map(({ _id }) => ({
                      fulfillmentGroupId: _id,
                      buyReturnLabel: !!value,
                    })),
                  );
                }}
                size="xs"
                value={buyReturnLabelCheckboxValue && !isMultiParcel}
              />
            </Flex>

            <Flex dir="column" gap="sm" justify="flex-start">
              <Flex>
                <RedoCheckbox
                  description={
                    <Flex>
                      <RedoExternalLink
                        fontSize="xxs"
                        showIcon={false}
                        text="Learn more"
                        textClassName={additionalFulfillmentCss.linkText}
                        url="https://faq.usps.com/s/article/What-is-Media-Mail-Book-Rate"
                      />
                    </Flex>
                  }
                  disabled={everythingDisabled || isEditingLockedStatus}
                  label="USPS Media Mail eligible"
                  setValue={(value) => {
                    updateRows(
                      fulfillmentGroups.map(
                        ({ _id, specialRateEligibility }) => ({
                          fulfillmentGroupId: _id,
                          specialRateEligibility: value
                            ? // If the box is checked, the option should always include media mail
                              includesLibraryMail(specialRateEligibility)
                              ? SpecialRateEligibility.MEDIA_MAIL_AND_LIBRARY_MAIL
                              : SpecialRateEligibility.MEDIA_MAIL
                            : // If the box is unchecked, we leave the library mail option (if it's already included)
                              includesLibraryMail(specialRateEligibility)
                              ? SpecialRateEligibility.LIBRARY_MAIL
                              : null,
                        }),
                      ),
                    );
                  }}
                  size="xs"
                  value={mediaMailCheckboxValue}
                />
              </Flex>
            </Flex>
            {(team.settings.outboundLabels.enabledCarrierAccounts
              ?.directToCarrierIntegrations?.dhl ||
              hasDhlByoaAccount) && (
              <Flex dir="column" gap="sm" justify="flex-start">
                <Flex>
                  <RedoCheckbox
                    description={
                      <Flex>
                        <RedoExternalLink
                          fontSize="xxs"
                          showIcon={false}
                          text="Learn more"
                          textClassName={additionalFulfillmentCss.linkText}
                          url="https://www.dhl.com/content/dam/dhl/local/us/dhl-ecommerce/documents/pdf/us-ecommerce-smartmail-bpm.pdf.coredownload.pdf"
                        />
                      </Flex>
                    }
                    disabled={everythingDisabled || isEditingLockedStatus}
                    label="DHL Bound Printed Matter eligible"
                    setValue={(value) => {
                      updateRows(
                        fulfillmentGroups.map(({ _id }) => ({
                          fulfillmentGroupId: _id,
                          specialRateEligibility: value
                            ? SpecialRateEligibility.DHL_BPM
                            : null,
                        })),
                      );
                    }}
                    size="xs"
                    value={dhlBpmCheckboxValue}
                  />
                </Flex>
              </Flex>
            )}
            <Flex dir="column" gap="sm" justify="flex-start">
              <RedoCheckbox
                description={
                  <Flex align="center" gap="xs">
                    <Text fontSize="xs" textColor="tertiary">
                      Carbon neutral
                    </Text>
                    <InfoTooltip
                      boxSize={16}
                      tooltip={{
                        title:
                          "Pay a small fee to offset carbon emissions. Not supported by all carriers",
                      }}
                    />
                  </Flex>
                }
                disabled={everythingDisabled || isEditingLockedStatus}
                setValue={(value) => {
                  updateRows(
                    fulfillmentGroups.map(({ _id }) => ({
                      fulfillmentGroupId: _id,
                      carbonNeutral: !!value,
                    })),
                  );
                }}
                size="xs"
                value={carbonNeutralCheckboxValue}
              />
            </Flex>
            <Flex dir="column" gap="sm" justify="flex-start">
              <RedoCheckbox
                description={
                  <Flex align="center" gap="xs">
                    <Text fontSize="xs" textColor="tertiary">
                      Direct delivery only
                    </Text>
                    <InfoTooltip
                      boxSize={16}
                      tooltip={{
                        title:
                          "Require direct delivery to the address on the label, preventing any potential routing to access points (UPS only)",
                      }}
                    />
                  </Flex>
                }
                disabled={everythingDisabled || isEditingLockedStatus}
                setValue={(value) => {
                  updateRows(
                    fulfillmentGroups.map(({ _id }) => ({
                      fulfillmentGroupId: _id,
                      directDeliveryOnly: !!value,
                    })),
                  );
                }}
                size="xs"
                value={directDeliveryOnlyCheckboxValue}
              />
            </Flex>
            <Flex dir="column" gap="sm" justify="flex-start">
              <RedoCheckbox
                description={
                  <Flex align="center" gap="xs">
                    <Text fontSize="xs" textColor="tertiary">
                      Override residential address
                    </Text>
                    <InfoTooltip
                      boxSize={16}
                      tooltip={{
                        title:
                          "Force the destination address to be marked as residential",
                      }}
                    />
                  </Flex>
                }
                disabled={everythingDisabled || isEditingLockedStatus}
                setValue={(value) => {
                  updateRows(
                    fulfillmentGroups.map(({ _id }) => ({
                      fulfillmentGroupId: _id,
                      overrideResidentialAddress: !!value,
                    })),
                  );
                }}
                size="xs"
                value={overrideResidentialAddressCheckboxValue}
              />
            </Flex>
            {canadaPostEnabled && (
              <Flex dir="column" gap="sm" justify="flex-start">
                <RedoSingleSelectDropdownInput<CanadaPostOption | null>
                  disabled={everythingDisabled || isEditingLockedStatus}
                  label={
                    <Flex align="center" gap="xs">
                      <Text fontSize="xs" textColor="tertiary">
                        Canada Post delivery option
                      </Text>
                      <InfoTooltip
                        boxSize={16}
                        tooltip={{
                          title:
                            "Delivery handling and age verification options for Canada Post shipments",
                        }}
                      />
                    </Flex>
                  }
                  options={canadaPostOptions}
                  optionSelected={({ item }) => {
                    updateRows(
                      fulfillmentGroups.map(({ _id }) => ({
                        fulfillmentGroupId: _id,
                        canadaPostOption: item.value,
                      })),
                    );
                  }}
                  selectedItem={
                    canadaPostOptions.find(
                      (opt) => opt.value === canadaPostOptionValue,
                    ) ?? canadaPostOptions[0]
                  }
                  size="xs"
                />
              </Flex>
            )}
            {dhlEcommerceEnabled && singleFulfillmentGroup && (
              <DhlEcommerceLabelSettings
                customLabelText1={
                  dhlEcommerceLabelOptions?.customLabelText1 ?? ""
                }
                customLabelText2={
                  dhlEcommerceLabelOptions?.customLabelText2 ?? ""
                }
                disabled={everythingDisabled || isEditingLockedStatus}
                key={[
                  singleFulfillmentGroup._id,
                  dhlEcommerceLabelOptions?.customLabelText1 ?? "",
                  dhlEcommerceLabelOptions?.customLabelText2 ?? "",
                  dhlEcommerceLabelOptions?.serviceEndorsement ?? "",
                ].join("|")}
                serviceEndorsement={
                  dhlEcommerceLabelOptions?.serviceEndorsement ?? null
                }
                updateOptions={updateDhlEcommerceLabelOptions}
              />
            )}
            {/* HIDE LIBRARY MAIL FOR NOW */}
            {/* <Flex>
                <RedoCheckbox
                  description={
                    <Flex>
                      <RedoExternalLink
                        text="Learn more"
                        url="https://faq.usps.com/s/article/What-are-Library-Mail-Service-and-Media-Mail-Service"
                      />
                    </Flex>
                  }
                  disabled={!canPurchaseEveryGroup}
                  label="USPS Library Mail eligible"
                  setValue={(value) => {
                    updateRows(
                      fulfillmentGroups.map(
                        ({ _id, specialRateEligibility }) => ({
                          fulfillmentGroupId: _id,
                          specialRateEligibility: value
                            ? // If the box is checked, the option should always include library mail
                              includesMediaMail(specialRateEligibility)
                              ? SpecialRateEligibility.MEDIA_MAIL_AND_LIBRARY_MAIL
                              : SpecialRateEligibility.LIBRARY_MAIL
                            : // If the box is unchecked, we leave the media mail option (if it's already included)
                              includesMediaMail(specialRateEligibility)
                              ? SpecialRateEligibility.MEDIA_MAIL
                              : null,
                        }),
                      ),
                    );
                  }}
                  size="xs"
                  value={libraryMailCheckboxValue}
                />
              </Flex> */}
          </>
        )}

        {isShipping && thirdPartyBillingAccounts.value && (
          <Flex dir="column" gap="sm">
            <RedoSingleSelectDropdownInput<string | null>
              disabled={everythingDisabled || isEditingLockedStatus}
              label="Third party billing account"
              options={[
                defaultThirdPartyBillingAccount,
                ...(thirdPartyBillingAccounts.value ?? [])
                  .filter(
                    (account) =>
                      !account.hidden ||
                      account._id === selectedThirdPartyBillingAccount?._id,
                  )
                  .map((account) => {
                    const item: RedoListItem<string | null> = {
                      id: account._id,
                      type: "text",
                      text: account.accountName,
                      value: account._id,
                    };

                    return item;
                  }),
              ]}
              optionSelected={({ item }) => {
                // Reset payment when we change account
                onShipmentPaymentOptionChange(item?.value ?? null, []);
              }}
              placeholder={
                mixedThirdPartyBillingAccounts
                  ? "{mixed}"
                  : "Select billing account"
              }
              selectedItem={
                mixedThirdPartyBillingAccounts
                  ? undefined
                  : selectedThirdPartyBillingAccount
                    ? {
                        id: selectedThirdPartyBillingAccount._id,
                        type: "text",
                        text: selectedThirdPartyBillingAccount.accountName,
                        value: selectedThirdPartyBillingAccount._id,
                      }
                    : defaultThirdPartyBillingAccount
              }
              size="xs"
            />
            <Text
              cursor="pointer"
              fontSize="xs"
              onClick={() => setAddBillingModalOpen(true)}
              textColor="tertiary"
              textDecoration="underline"
              w="fit-content"
            >
              + Add new third party billing account
            </Text>
            <AddThirdPartyBillingModal
              isOpen={addBillingModalOpen}
              onAddAccount={handleAddAccount}
              onClose={() => setAddBillingModalOpen(false)}
              showUseOnceOption
            />
            {!!selectedThirdPartyBillingAccount && (
              <Flex dir="column" gap="sm" justify="flex-start">
                <RedoCheckbox
                  description={
                    <Flex align="center" gap="xs">
                      <Text fontSize="xs" textColor="tertiary">
                        Only bill third party for VAT
                      </Text>
                      <InfoTooltip
                        boxSize={16}
                        tooltip={{
                          title:
                            "UPS Split Duty and VAT - shipper pays for shipping, duties and taxes. Third party pays for VAT only",
                        }}
                      />
                    </Flex>
                  }
                  disabled={everythingDisabled || isEditingLockedStatus}
                  setValue={(value) => {
                    onShipmentPaymentOptionChange(
                      selectedThirdPartyBillingAccount?._id ?? null,
                      value ? [PaymentResponsibility.VAT] : [],
                    );
                  }}
                  size="xs"
                  value={splitDutyAndVatCheckboxValue}
                />
              </Flex>
            )}
          </Flex>
        )}
      </Flex>
    );
  },
);

function DhlEcommerceLabelSettings({
  customLabelText1,
  customLabelText2,
  disabled,
  serviceEndorsement,
  updateOptions,
}: {
  customLabelText1: string;
  customLabelText2: string;
  disabled: boolean;
  serviceEndorsement: DhlEcommerceServiceEndorsement | null;
  updateOptions: (options: Partial<DhlEcommerceLabelOptions>) => void;
}) {
  const [customLabelText1Draft, setCustomLabelText1Draft] =
    useState(customLabelText1);
  const [customLabelText2Draft, setCustomLabelText2Draft] =
    useState(customLabelText2);

  return (
    <Flex dir="column" gap="sm" justify="flex-start">
      <RedoTextInput
        fullWidth
        label="DHL custom label text 1"
        onBlur={() =>
          updateOptions({ customLabelText1: customLabelText1Draft })
        }
        placeholder="Optional label note"
        setValue={setCustomLabelText1Draft}
        size="sm"
        state={disabled ? "disabled" : "default"}
        value={customLabelText1Draft}
      />
      <RedoTextInput
        fullWidth
        label="DHL custom label text 2"
        onBlur={() =>
          updateOptions({ customLabelText2: customLabelText2Draft })
        }
        placeholder="Optional label note"
        setValue={setCustomLabelText2Draft}
        size="sm"
        state={disabled ? "disabled" : "default"}
        value={customLabelText2Draft}
      />
      <RedoSingleSelectDropdownInput<DhlEcommerceServiceEndorsement | null>
        disabled={disabled}
        label="DHL ancillary endorsement"
        options={dhlServiceEndorsementOptions}
        optionSelected={({ item }) => {
          updateOptions({ serviceEndorsement: item.value });
        }}
        selectedItem={
          dhlServiceEndorsementOptions.find(
            (option) => option.value === serviceEndorsement,
          ) ?? dhlServiceEndorsementOptions[0]
        }
        size="xs"
      />
    </Flex>
  );
}
